import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';
export const ImageLightbox = ({ images, currentIndex, onClose, onNavigate }) => {
    const currentImage = images[currentIndex];
    const canGoBack = currentIndex > 0;
    const canGoForward = currentIndex < images.length - 1;
    const handleKeyDown = useCallback((e) => {
        // Stop propagation to prevent CommentsModal from also closing
        e.stopImmediatePropagation();
        switch (e.key) {
            case 'Escape':
                onClose();
                break;
            case 'ArrowLeft':
                if (canGoBack)
                    onNavigate(currentIndex - 1);
                break;
            case 'ArrowRight':
                if (canGoForward)
                    onNavigate(currentIndex + 1);
                break;
        }
    }, [onClose, onNavigate, currentIndex, canGoBack, canGoForward]);
    useEffect(() => {
        // Use capture phase so this handler runs before the modal's handler
        window.addEventListener('keydown', handleKeyDown, true);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
            document.body.style.overflow = '';
        };
    }, [handleKeyDown]);
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = currentImage.presignedUrl;
        link.download = currentImage.filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    if (!currentImage)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-[100] flex items-center justify-center bg-black/90", onClick: handleBackdropClick, children: [_jsxs("div", { className: "absolute top-4 left-4 right-4 flex items-center justify-between z-10", children: [_jsxs("div", { className: "flex items-center gap-4", children: [_jsxs("span", { className: "text-sm px-3 py-1 rounded bg-black/50", style: { color: 'var(--sand)' }, children: [currentIndex + 1, " / ", images.length] }), _jsx("span", { className: "text-sm truncate max-w-[300px]", style: { color: 'var(--dew)' }, title: currentImage.filename, children: currentImage.filename })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { onClick: handleDownload, className: "p-2 rounded-full hover:bg-white/20 transition-colors", style: { color: 'var(--sand)' }, title: "Download", children: _jsx(Download, { size: 20 }) }), _jsx("button", { onClick: onClose, className: "p-2 rounded-full hover:bg-white/20 transition-colors", style: { color: 'var(--sand)' }, title: "Close (Escape)", children: _jsx(X, { size: 20 }) })] })] }), _jsxs("div", { className: "relative flex items-center justify-center w-full h-full p-16", children: [canGoBack && (_jsx("button", { onClick: () => onNavigate(currentIndex - 1), className: "absolute left-4 p-3 rounded-full hover:bg-white/20 transition-colors", style: { color: 'var(--sand)' }, title: "Previous (Left arrow)", children: _jsx(ChevronLeft, { size: 32 }) })), _jsx("img", { src: currentImage.presignedUrl, alt: currentImage.filename, className: "max-w-full max-h-full object-contain", style: { maxHeight: 'calc(100vh - 8rem)' } }), canGoForward && (_jsx("button", { onClick: () => onNavigate(currentIndex + 1), className: "absolute right-4 p-3 rounded-full hover:bg-white/20 transition-colors", style: { color: 'var(--sand)' }, title: "Next (Right arrow)", children: _jsx(ChevronRight, { size: 32 }) }))] }), _jsx("div", { className: "absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2", children: images.map((_, idx) => (_jsx("button", { onClick: () => onNavigate(idx), className: `w-2 h-2 rounded-full transition-colors ${idx === currentIndex ? 'bg-white' : 'bg-white/30 hover:bg-white/50'}` }, idx))) })] }));
};
